import React, { useState } from "react";
import { Car } from "lucide-react";

// Production-safe image handling.
// Policy: only images served from our own CMS / object storage (or explicit
// non-stock URLs) are treated as accurate. Seeded external stock photos
// (unsplash / pexels / etc.) are NOT accurate for specific Indian-market
// models, so we render a clean branded placeholder instead of a wrong car.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const STOCK_HOSTS = [
  "images.unsplash.com", "unsplash.com", "source.unsplash.com",
  "images.pexels.com", "pexels.com", "pixabay.com", "cdn.pixabay.com",
];

function isTrusted(src) {
  if (!src || typeof src !== "string") return false;
  const s = src.trim();
  if (!s) return false;
  if (s.startsWith("data:image/")) return true;
  if (s.startsWith("/")) return true; // uploaded / same-origin asset
  if (BACKEND && s.startsWith(BACKEND)) return true;
  try {
    const host = new URL(s).hostname.toLowerCase();
    if (STOCK_HOSTS.some((d) => host === d || host.endsWith(`.${d}`))) return false;
    return true; // other explicit URLs (e.g. official CDN) are allowed through
  } catch {
    return false;
  }
}

function Placeholder({ label, compact }) {
  return (
    <div
      data-testid="image-placeholder"
      className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-center px-2 bg-gradient-to-br from-[#EFF6FF] via-white to-[#ECFEFF] dark:from-secondary dark:via-background dark:to-secondary"
    >
      <div className={`${compact ? "w-6 h-6 rounded-lg" : "w-10 h-10 rounded-2xl"} bg-gradient-to-br from-[#2563EB] to-[#06B6D4] grid place-items-center shadow-sm`}>
        <Car className={compact ? "w-3 h-3 text-white" : "w-5 h-5 text-white"} />
      </div>
      {!compact && (
        <>
          <div className="text-[11px] font-semibold tracking-tight text-[#1F2937] dark:text-foreground">TrendingNewCar</div>
          <div className="text-[10px] text-muted-foreground leading-tight max-w-[90%] line-clamp-2">{label || "Image coming soon"}</div>
        </>
      )}
    </div>
  );
}

// Drop-in replacement for <img> inside an existing aspect-ratio / sized box.
// Renders a fill container so there is no layout shift.
export default function SmartImage({ src, alt = "", className = "", fit = "cover", eager = false, compact = false, label }) {
  const trusted = isTrusted(src);
  const [status, setStatus] = useState(trusted ? "loading" : "placeholder");

  return (
    <div className="relative w-full h-full overflow-hidden bg-[#F3F6FA] dark:bg-secondary">
      {(status === "loading" || status === "loaded") && (
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => setStatus("loaded")}
          onError={() => setStatus("placeholder")}
          className={`w-full h-full object-${fit} transition-opacity duration-300 ${status === "loaded" ? "opacity-100" : "opacity-0"} ${className}`}
        />
      )}
      {status === "loading" && <div className="absolute inset-0 shimmer" aria-hidden="true" />}
      {status === "placeholder" && <Placeholder label={label || alt} compact={compact} />}
    </div>
  );
}
